<script lang="ts">
    import { createEventDispatcher } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";
    import ArrowHeadIcon from "@components/icons/ArrowHeadIcon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import Scrollable from "@components/Scrollable.svelte";
    import T from "@components/T.svelte";

    import type { ProductViewControllerInterface } from "./types";

    export let productViewController: ProductViewControllerInterface;
    export let showControlls: boolean;
    const dispatch = createEventDispatcher();
    const handleClickNextProductTag = () => {
        productViewController.nextProductTag();
    };
    const handleClickPrevProductTag = () => {
        productViewController.prevProductTag();
    };
    const handleCloseProductPreview = () => {
        productViewController.close();
    };
    const handleClose = () => {
        dispatch("close");
    };
</script>

<div tabindex="-1" class="gw-post-modal-product-view">
    <div class="gw-post-modal-product-view__left">
        <slot name="productImage" />
        {#if showControlls}
            <BaseButton
                class="gw-post-modal-product-view__button--prev"
                ariaLabel="Go to prev tagged product"
                on:click={handleClickPrevProductTag}
            >
                <span
                    class="gw-post-modal-product-view__button-icon gw-post-modal-product-view__button-icon--left"
                >
                    <Icon width={12} height={12}>
                        <ArrowHeadIcon />
                    </Icon>
                </span>
            </BaseButton>
            <BaseButton
                class="gw-post-modal-product-view__button--next"
                ariaLabel="Go to next tagged product"
                on:click={handleClickNextProductTag}
            >
                <span class="gw-post-modal-product-view__button-icon">
                    <Icon width={12} height={12}>
                        <ArrowHeadIcon />
                    </Icon>
                </span>
            </BaseButton>
        {/if}
    </div>
    <div class="gw-post-modal-product-view__right">
        <div class="gw-post-modal-product-view__header">
            <BaseButton
                class="gw-post-modal-product-view__button"
                ariaLabel="Back to instagram post modal"
                on:click={handleCloseProductPreview}
            >
                <span
                    class="gw-post-modal-product-view__button-icon gw-post-modal-product-view__button-icon--left"
                >
                    <Icon width={12} height={12}>
                        <ArrowHeadIcon />
                    </Icon>
                </span>
                <T namespace="common.PostModal" translationKey="back" />
            </BaseButton>
            <BaseButton
                class="gw-post-modal-product-view__close"
                ariaLabel="Close post modal"
                on:click={handleClose}
            >
                <Icon width={12} height={12}>
                    <CloseIcon />
                </Icon>
            </BaseButton>
        </div>
        <slot name="activePostLink" />
        <div class="gw-post-modal-product-view__content">
            <Scrollable class="gw-post-modal-product-view__scrollable">
                <slot name="productInfo" />
                <slot />
            </Scrollable>
        </div>
    </div>
</div>

<style>
    .gw-post-modal-product-view {
        display: flex;
        outline: none;
    }

    .gw-post-modal-product-view__content {
        position: relative;
        height: calc(100% - 100px);
    }

    .gw-post-modal-product-view__left {
        position: relative;
        width: 100%;
        height: 100vh;
        overflow: hidden;
        background-color: var(--gw-black-bg);
    }
    .gw-post-modal-product-view__right {
        position: relative;
        display: flex;
        flex-direction: column;
        min-width: 405px;
        padding: 0 16px;
        background-color: var(--gw-modal-dialog-bg);
    }
    .gw-post-modal-product-view__right
        :global(.gw-post-modal-product-view__scrollable) {
        display: flex;
        position: absolute;
        top: 0;
        left: 0;
        flex-direction: column;
        width: 100%;
        height: 100%;
    }
    .gw-post-modal-product-view__header {
        display: flex;
        justify-content: space-between;
        padding: 20px 0 18px 0;
    }

    .gw-post-modal-product-view :global(.gw-post-modal-product-view__button) {
        display: flex;
        align-items: center;
        padding: 0;
        font-size: var(--gw-font-size-lg);
        column-gap: 10px;
        color: var(--gw-modal-close-icon-color);
        font-weight: normal;
        text-transform: capitalize;
    }

    .gw-post-modal-product-view
        :global(.gw-post-modal-product-view__button--prev) {
        position: absolute;
        top: 50%;
        left: 2%;
        width: 32px;
        height: 32px;
        color: var(--gw-grey-reset-button-color);
        background: var(--gw-grey-btn-bg);
        backdrop-filter: blur(8px);
        transform: rotate(180deg);
        border-radius: 50%;
    }

    .gw-post-modal-product-view
        :global(.gw-post-modal-product-view__button--next) {
        position: absolute;
        top: 50%;
        right: 2%;
        width: 32px;
        height: 32px;
        color: var(--gw-grey-reset-button-color);
        background: var(--gw-grey-btn-bg);
        backdrop-filter: blur(8px);
        border-radius: 50%;
    }

    .gw-post-modal-product-view :global(.gw-post-modal-product-view__close) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background-color: var(--gw-lightgrey-bg);
        border-radius: 50%;
        color: var(--gw-modal-close-icon-color);
    }

    .gw-post-modal-product-view__button-icon--left {
        transform: rotateY(180deg);
    }

    .gw-post-modal-product-view__button-icon {
        line-height: 0;
    }

    .gw-post-modal-product-view__button-icon :global(svg) {
        stroke: var(--gw-grey-reset-button-color);
    }

    @media all and (min-width: 1100px) {
        .gw-post-modal-product-view__left {
            height: 90vh;
        }
    }

    @media all and (max-width: 768px) {
        .gw-post-modal-product-view {
            flex-flow: column;
        }

        .gw-post-modal-product-view__right {
            min-width: unset;
        }

        .gw-post-modal-product-view__right
            :global(.gw-post-modal-product-view__scrollable) {
            position: relative;
        }
        .gw-post-modal-product-view
            :global(.gw-post-modal-product-view__close) {
            position: absolute;
            top: 24px;
            right: 16px;
            color: var(--gw-grey-reset-button-color);
            background: var(--gw-grey-btn-bg);
            backdrop-filter: blur(8px);
        }
        .gw-post-modal-product-view__right {
            position: initial;
        }
    }
</style>
